import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { Icons } from "@/components/ui/icons";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { User } from "lucide-react";
import NequiButton from "@/components/NequiButton";
import WompiButton from "@/components/WompiButton";
import { IS_NEQUI_ENABLED } from "@/lib/config/featureFlags";

const CartPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    selectedPlan,
    clearCart,
    initiatePayment,
    isProcessingPayment,
    applyPromoCode,
    removePromoCode,
    promoCode,
    discount,
  } = useCart();
  const { toast } = useToast();
  const [promoInput, setPromoInput] = useState("");
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);

  useEffect(() => {
    // If no plan is selected, redirect to plans
    if (!selectedPlan) {
      navigate("/planes");
      return;
    }

    // If not logged in, redirect to login with return URL
    if (!user) {
      // Store plan selection before redirecting
      localStorage.setItem("selectedPlan", JSON.stringify(selectedPlan));
      navigate("/login?returnUrl=/planes");
      return;
    }
  }, [selectedPlan, user, navigate]);

  const handleApplyPromo = async () => {
    if (!promoInput.trim()) return;

    setIsApplyingPromo(true);
    try {
      const result = await applyPromoCode(promoInput);
      toast({
        title: result.success ? "Éxito" : "Error",
        description: result.message,
        variant: result.success ? "default" : "destructive",
      });
      if (result.success) {
        setPromoInput("");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Ocurrió un error al aplicar el código promocional",
        variant: "destructive",
      });
    } finally {
      setIsApplyingPromo(false);
    }
  };

  const handleRemovePromo = () => {
    removePromoCode();
    toast({
      title: "Código removido",
      description: "El código promocional ha sido removido",
    });
  };

  if (!selectedPlan || !user) {
    return null;
  }

  const subtotal = selectedPlan.price;
  const tax = subtotal * 0.19;
  const total = subtotal + tax - discount;

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <h1 className="text-3xl font-bold mb-8">
        MI CARRITO DE COMPRAS ({selectedPlan ? "1" : "0"})
      </h1>

      {/* User Status Section */}
      <div className="bg-white rounded-lg shadow-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="font-medium">Estado de sesión</p>
              {user ? (
                <p className="text-sm text-green-600">
                  Conectado como: {user.email}
                </p>
              ) : (
                <p className="text-sm text-red-600">No has iniciado sesión</p>
              )}
            </div>
          </div>
          {!user && (
            <Button
              onClick={() => navigate("/login?returnUrl=/planes")}
              className="bg-blue-600 hover:bg-blue-700 text-white">
              Iniciar Sesión
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Cart Items - Left Column */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-lg p-6 mb-4">
            <div className="flex items-start gap-4">
              {/* Plan Image/Icon */}
              <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center">
                <Icons.creditCard className="w-12 h-12 text-gray-400" />
              </div>

              {/* Plan Details */}
              <div className="flex-grow">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-xl font-bold mb-1">
                      Plan {selectedPlan.name}
                    </h2>
                    <p className="text-gray-600 text-sm">Suscripción Mensual</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold">
                      ${selectedPlan.price.toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 mt-4">
                  <button
                    onClick={() => {
                      clearCart();
                      navigate("/planes");
                    }}
                    className="text-gray-500 hover:text-gray-700 flex items-center gap-1">
                    <Icons.trash className="w-4 h-4" />
                    <span>Eliminar</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Benefits List */}
            <div className="mt-6">
              <h3 className="font-semibold mb-2">Beneficios Incluidos:</h3>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li className="flex items-center gap-2">
                  <Icons.check className="w-4 h-4 text-green-500" />
                  Análisis legal avanzado
                </li>
                <li className="flex items-center gap-2">
                  <Icons.check className="w-4 h-4 text-green-500" />
                  Generación de documentos ilimitada
                </li>
                <li className="flex items-center gap-2">
                  <Icons.check className="w-4 h-4 text-green-500" />
                  20 consultas legales al mes
                </li>
                <li className="flex items-center gap-2">
                  <Icons.check className="w-4 h-4 text-green-500" />
                  Soporte prioritario
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Order Summary - Right Column */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-bold mb-4">Resumen del Pedido</h2>

            {/* Promo Code Section */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold">APLICAR CÓDIGO PROMO</span>
                <Icons.chevronDown className="w-4 h-4" />
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Ingresa el código"
                  className="flex-grow px-3 py-2 border rounded-md"
                  value={promoInput}
                  onChange={(e) => setPromoInput(e.target.value)}
                  disabled={isApplyingPromo || !!promoCode}
                />
                <Button
                  onClick={handleApplyPromo}
                  disabled={
                    isApplyingPromo || !promoInput.trim() || !!promoCode
                  }
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors">
                  {isApplyingPromo ? "Aplicando..." : "APLICAR"}
                </Button>
              </div>
              {promoCode && (
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="text-green-600">
                    Código aplicado: {promoCode.code}
                  </span>
                  <button
                    onClick={handleRemovePromo}
                    className="text-red-500 hover:text-red-700">
                    Eliminar
                  </button>
                </div>
              )}
            </div>

            {/* Order Details */}
            <div className="space-y-3 border-t border-b border-gray-200 py-4 mb-4">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-semibold">
                  ${subtotal.toLocaleString()}
                </span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Descuento</span>
                  <span className="font-semibold">
                    -${discount.toLocaleString()}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">IVA (19%)</span>
                <span className="font-semibold">${tax.toLocaleString()}</span>
              </div>
            </div>

            {/* Total */}
            <div className="flex justify-between items-center mb-6">
              <span className="text-lg font-bold">Total</span>
              <span className="text-2xl font-bold">
                ${total.toLocaleString()}
              </span>
            </div>

            {/* Payment Methods */}
            <div className="mb-6">
              <h3 className="font-medium mb-2">Medios de Pago Disponibles:</h3>
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 border rounded text-center">
                  <span className="text-sm">Tarjeta de Crédito</span>
                </div>
                <div className="p-2 border rounded text-center">
                  <span className="text-sm">PSE</span>
                </div>
                {IS_NEQUI_ENABLED && (
                  <div className="p-2 border rounded text-center">
                    <span className="text-sm">Nequi</span>
                  </div>
                )}
                <div className="p-2 border rounded text-center">
                  <span className="text-sm">Bancolombia</span>
                </div>
              </div>
            </div>

            {/* Checkout Payment Button */}
            {IS_NEQUI_ENABLED ? (
              <NequiButton
                amount={total}
                planId={selectedPlan.name.toLowerCase()}
                planName={selectedPlan.name}
                buttonText="PAGAR CON NEQUI"
                className="w-full py-3 px-4 mb-4 rounded-md text-lg font-semibold h-12"
                onSuccess={() => {
                  clearCart();
                  navigate("/dashboard");
                }}
              />
            ) : (
              <WompiButton
                amount={total}
                planId={selectedPlan.id || selectedPlan.name.toLowerCase()}
                planName={selectedPlan.name}
                buttonText="PAGAR AHORA"
                className="w-full py-3 px-4 mb-4 rounded-md text-lg font-semibold h-12"
                onSuccess={() => {
                  clearCart();
                  navigate("/dashboard");
                }}
              />
            )}

            {/* Wompi Form Container (Comentado)
            <div id="wompi-button-container" className="w-full" />
            */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
